import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { getParentNotifications } from '../services/parentService';
import { getNotifications } from '../services/notificationService';
import { getActivitiesToVerify } from '../services/teacherService';
// import type { Notification as ApiNotification } from '../types/api';

type BadgeType = 'new' | 'pending' | 'unread' | string;

export const useBadgeCount = (type: BadgeType): number => {
  const { user } = useAuth();
  
  // For parents/viewers - count unread notifications
  const { data: parentNotifications } = useQuery({
    queryKey: ['notifications', 'parent', 'unread'],
    queryFn: () => getParentNotifications({ page: 1, per_page: 100 }),
    enabled: !!user && (user.role === 'parent' || user.role === 'viewer') && type === 'unread',
    select: (data: any) => data.items?.filter((n: any) => !n.read_at).length || 0,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // 1 minute
  });

  // For teachers - count pending verifications
  const { data: pendingVerifications } = useQuery({
    queryKey: ['verifications', 'pending'],
    queryFn: () => getActivitiesToVerify(),
    enabled: !!user && user.role === 'teacher' && type === 'pending',
    select: (data: any) => data.items?.length || data.pagination?.total_count || 0,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  // For admins - count new alerts or notifications
  const { data: adminNotifications } = useQuery({
    queryKey: ['notifications', 'admin', type],
    queryFn: () => getNotifications({ unread: true, page: 1, per_page: 100 }),
    enabled: !!user && (user.role === 'admin' || user.role === 'super_admin') && (type === 'new' || type === 'unread'),
    select: (data) => data.pagination?.total_count || 0,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  // Return appropriate count based on user role and badge type
  switch (type) {
    case 'unread':
      if (user?.role === 'parent' || user?.role === 'viewer') {
        return parentNotifications || 0;
      }
      return adminNotifications || 0;
    
    case 'pending':
      if (user?.role === 'teacher') {
        return pendingVerifications || 0;
      }
      return 0;
    
    case 'new':
      if (user?.role === 'admin' || user?.role === 'super_admin') {
        return adminNotifications || 0;
      }
      return 0;
    
    default:
      return 0;
  }
};